import React, { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { listPosts, createPost, updatePost, deletePost, getPost, listUsers, addUser, removeUser } from "../api";
import logo from "../images/mb-m-500-white.png";

export default function Editor() {
  const { logout, getToken } = useAuth();
  const [tab, setTab] = useState("posts"); // "posts" | "users"

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="bg-primary-animated text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logo} alt="" className="w-8" />
          <span className="font-bold text-lg">Metabetta CMS</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/blog" className="text-sm text-gray-300 hover:text-white transition">
            View Blog
          </a>
          <a href="/" className="text-sm text-gray-300 hover:text-white transition">
            Main Site
          </a>
          <button
            onClick={logout}
            className="text-sm bg-white/10 px-4 py-2 rounded hover:bg-white/20 transition"
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-screen-lg mx-auto pt-6 px-6">
        <div className="flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setTab("posts")}
            className={`px-4 py-2 text-sm font-medium rounded-t transition ${
              tab === "posts" ? "bg-white border border-b-0 border-gray-200 text-primary-500" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Blog Posts
          </button>
          <button
            onClick={() => setTab("users")}
            className={`px-4 py-2 text-sm font-medium rounded-t transition ${
              tab === "users" ? "bg-white border border-b-0 border-gray-200 text-primary-500" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Users
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-screen-lg mx-auto py-6 px-6">
        {tab === "posts" ? <PostsManager getToken={getToken} /> : <UsersManager getToken={getToken} />}
      </main>
    </div>
  );
}

// === POSTS MANAGER ===
function PostsManager({ getToken }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyPost = { title: "", slug: "", excerpt: "", content: "", author: "", published: false };

  const loadPosts = async () => {
    setLoading(true);
    try {
      setPosts(await listPosts());
    } catch {
      setPosts([]);
    }
    setLoading(false);
  };

  useEffect(() => { loadPosts(); }, []);

  const handleNew = () => setEditing({ ...emptyPost });

  const handleEdit = async (slug) => {
    try {
      setEditing(await getPost(slug));
    } catch {
      alert("Failed to load post");
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    try {
      const token = await getToken();
      await deletePost(slug, token);
      await loadPosts();
    } catch {
      alert("Failed to delete");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.slug) {
      alert("Title and slug are required");
      return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      if (editing.date) {
        await updatePost(editing.slug, editing, token);
      } else {
        await createPost(editing, token);
      }
      setEditing(null);
      await loadPosts();
    } catch (err) {
      alert("Failed to save: " + (err.message || "unknown error"));
    }
    setSaving(false);
  };

  const generateSlug = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  if (editing) {
    return (
      <div>
        <button onClick={() => setEditing(null)} className="text-primary-500 hover:underline text-sm mb-6 inline-block">
          &larr; Back to posts
        </button>
        <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-xl font-bold">{editing.date ? "Edit Post" : "New Post"}</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={editing.title}
              onChange={(e) => {
                const title = e.target.value;
                setEditing({ ...editing, title, slug: editing.date ? editing.slug : generateSlug(title) });
              }}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={editing.slug}
              onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-primary-500 font-mono text-sm"
              required
              disabled={!!editing.date}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
            <input
              type="text"
              value={editing.author}
              onChange={(e) => setEditing({ ...editing, author: e.target.value })}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
            <textarea
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              rows={2}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-primary-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content (Markdown)</label>
            <textarea
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              rows={16}
              className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-primary-500 font-mono text-sm resize-y"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editing.published}
              onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
              className="w-4 h-4 text-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Published</span>
          </label>
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-500 text-white font-bold px-6 py-2 rounded hover:bg-primary-700 transition disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Post"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="text-gray-600 px-6 py-2 rounded border hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Blog Posts</h1>
        <button onClick={handleNew} className="bg-primary-500 text-white font-bold px-6 py-2 rounded hover:bg-primary-700 transition">
          + New Post
        </button>
      </div>
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No posts yet. Create your first one!</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {posts.map((post) => (
            <div key={post.slug} className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{post.title}</h3>
                <p className="text-xs text-gray-400 mt-1">
                  {post.date && new Date(post.date).toLocaleDateString()} &middot;{" "}
                  {post.published ? <span className="text-green-600">Published</span> : <span className="text-yellow-600">Draft</span>}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(post.slug)} className="text-sm text-primary-500 hover:underline">Edit</button>
                <button onClick={() => handleDelete(post.slug)} className="text-sm text-red-500 hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === USERS MANAGER ===
function UsersManager({ getToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      setUsers(await listUsers(token));
    } catch {
      setUsers([]);
    }
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    setAdding(true);
    try {
      const token = await getToken();
      await addUser(newEmail, token);
      setNewEmail("");
      await loadUsers();
    } catch (err) {
      alert("Failed to add user: " + (err.response?.data?.error || err.message));
    }
    setAdding(false);
  };

  const handleRemove = async (username, email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      const token = await getToken();
      await removeUser(username, token);
      await loadUsers();
    } catch {
      alert("Failed to remove user");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Allowed Users</h1>
      <p className="text-gray-500 text-sm mb-6">
        Users listed here can sign in with Google. Add their email to grant access.
      </p>

      {/* Add user form */}
      <form onSubmit={handleAdd} className="flex gap-3 mb-6">
        <input
          type="email"
          placeholder="Email address"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          required
          className="flex-1 border border-gray-300 rounded px-4 py-2 text-sm focus:outline-none focus:border-primary-500"
        />
        <button
          type="submit"
          disabled={adding}
          className="bg-primary-500 text-white font-bold px-6 py-2 rounded hover:bg-primary-700 transition disabled:opacity-50 text-sm"
        >
          {adding ? "Adding..." : "Add User"}
        </button>
      </form>

      {/* Users list */}
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No users found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {users.map((user) => (
            <div key={user.username} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Status: {user.status} &middot; Added: {new Date(user.created).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => handleRemove(user.username, user.email)}
                className="text-sm text-red-500 hover:underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
